import { events, type EventsChannel } from 'aws-amplify/data';
import { IRemoteVote, IssuedVoteDoc, VotesResults } from './apis/vote';
import GameRecordId from '../core/game-record-id.type';
import { extractGameIdentifier, getAuthToken, getNamespace, publishEvent, extractSessionId } from '../utils/amplify-config';
import axios from 'axios';

const API_BASE_URL = process.env.API_ENDPOINT;

type SubscriptionPayload = {
    id: string;
    type: 'data';
    event: IssuedVoteDoc;
};

/**
 * Amplify Events implementation of IRemoteVote
 */
class AmplifyVote implements IRemoteVote {
    private channels: Map<string, EventsChannel> = new Map();
    private subscriptions: Map<string, any> = new Map();

    private async getOrCreateChannel(
        gameRecordId: GameRecordId
    ): Promise<EventsChannel> {
        if (this.channels.has(gameRecordId)) {
            return this.channels.get(gameRecordId)!;
        }

        const gameIdentifier = extractGameIdentifier(gameRecordId);
        const channelName = `${getNamespace()}/votes/${gameIdentifier}`;
        console.log('Connecting to channel:', channelName);

        const channel = await events.connect(channelName, { authToken: getAuthToken() });

        this.channels.set(gameRecordId, channel);
        return channel;
    }

    /**
     * Subscribe to vote submissions
     */
    onVoteSnapshot(gameRecordId: GameRecordId, cb: (doc: IssuedVoteDoc) => void) {
        this.unsubscribe(gameRecordId);

        const promise = this.getOrCreateChannel(gameRecordId)
            .then((channel) => {
                // If cancelled while connecting, don't subscribe
                if (!this.subscriptions.has(gameRecordId)) return;

                const subscription = channel.subscribe({
                    next: ({ event: voteDoc }: SubscriptionPayload) => {
                        cb(initIssuedVoteDoc(voteDoc));
                    },
                    error: (error: Error) => {
                        console.error(`Error in vote channel ${gameRecordId}:`, error);
                    },
                });

                this.subscriptions.set(gameRecordId, subscription);
            })
            .catch((error) => {
                console.error(`Failed to create vote channel ${gameRecordId}:`, error);
                this.subscriptions.delete(gameRecordId);
            });

        // Store promise to indicate "connecting"
        this.subscriptions.set(gameRecordId, promise);

        return () => this.unsubscribe(gameRecordId);
    }

    private unsubscribe(key: string) {
        if (this.subscriptions.has(key)) {
            const sub = this.subscriptions.get(key);
            // If it's a subscription object
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
            // If it's a pending promise, we just remove it from the map.
            // The .then() block checks the map before subscribing, so it will respect this removal.
            this.subscriptions.delete(key);
        }
    }

    /**
     * Get all votes for a game (REST API)
     */
    async getVotes(gameRecordId: string): Promise<IssuedVoteDoc[]> {
        console.log('🗳️ [VoteEvents] Fetching votes for game:', gameRecordId);
        const encodedGameRecordId = encodeURIComponent(gameRecordId);
        const response = await axios.get(`${API_BASE_URL}/games/${encodedGameRecordId}/votes`);
        console.log('🗳️ [VoteEvents] Fetched votes:', response.data);
        return response.data;
    }

    /**
     * Save a vote (REST API + AppSync Events)
     */
    async saveVote(
        gameId: string,
        id: string,
        issuedVote: IssuedVoteDoc
    ): Promise<void> {
        const [playerId, votingSession] = id.split(':');
        const voteId = id;

        // 1. Execute REST API call
        await axios.post(`${API_BASE_URL}/votes`, {
            gameRecordId: gameId,
            voteId,
            issuedVote,
        });

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameId);
        const sessionId = extractSessionId(gameId);

        await publishEvent(`/votes/${gameIdentifier}`, {
            action: 'vote-saved',
            gameId,
            voteId,
            ...issuedVote,  // Spread to top level for initIssuedVoteDoc to work correctly
        });

        // Also publish to session channel for debrief updates
        await publishEvent(`/session/${sessionId}`, {
            type: 'vote-updated',
            data: {
                gameId,
                gameRecordId: gameId,
                voteId,
                ...issuedVote,
            },
        });
    }

    /**
     * Save vote results (REST API + AppSync Events)
     */
    async saveVotesResults(
        gameRecordId: GameRecordId,
        votesResults: VotesResults
    ): Promise<void> {
        // 1. Execute REST API call
        await axios.put(
            `${API_BASE_URL}/games/${gameRecordId}/votes-results`,
            votesResults
        );

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameRecordId);
        const sessionId = extractSessionId(gameRecordId);

        await publishEvent(`/game/${gameIdentifier}`, {
            action: 'votes-results-saved',
            gameRecordId,
            votesResults,
        });

        // Also publish to session channel for debrief updates
        await publishEvent(`/session/${sessionId}`, {
            action: 'votes-results-updated',
            gameRecordId,
            votesResults,
        });
    }

    /**
     * Clean up all channels and subscriptions
     */
    cleanup(): void {
        this.subscriptions.forEach((sub) => {
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
        });
        this.subscriptions.clear();
        this.channels.clear();
    }
}

const initIssuedVoteDoc = (plain: Partial<IssuedVoteDoc>): IssuedVoteDoc => ({
    session: '--null--',
    voter: '--null--',
    vote: '--null--',
    isAbstained: false,
    motivation: '',
    ...plain,
});

export const vote = new AmplifyVote();